import { provideHttpClient, withFetch, withInterceptors, withJsonpSupport } from '@angular/common/http';
import {
  ApplicationConfig,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { AppCustomTranslateParser } from '@core/custom-translate-parser';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { httpErrorInterceptor } from '@core/interceptors/error-interceptor';
import { KeycloakService } from '@core/services/auth/keycloak.service';
import { environment } from '@environments/environment';
import { provideTranslateParser, provideTranslateService } from '@ngx-translate/core';
import { ToastrModule } from '@openng/ngx-toastr';
import appRoutes from 'src/app/app-routes';
import { defineCustomElements as tegel } from '@scania/tegel/loader';
import { defineCustomElements as scope } from '@myscania/scope/dist/loader';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideHighcharts } from 'highcharts-angular';
import { highchartScaniaTheme } from '@shared/utils/highChartsScaniaTheme';

tegel(window);

scope();

if (environment.production) {
  enableProdMode();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideAppInitializer(() => {
      const keycloakService = inject(KeycloakService);
      return keycloakService.init();
    }),
    provideRouter(appRoutes, withViewTransitions(), withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideHttpClient(withJsonpSupport(), withFetch(), withInterceptors([httpErrorInterceptor, authInterceptor])),
    importProvidersFrom(
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-right',
        preventDuplicates: true,
        closeButton: true,
      }),
      BrowserAnimationsModule,
    ),

    provideTranslateService({
      fallbackLang: 'en-GB',
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
      parser: provideTranslateParser(AppCustomTranslateParser),
    }),
    provideHighcharts({
      options: highchartScaniaTheme,
      modules: () => {
        return [
          import('highcharts/esm/modules/annotations'),
          import('highcharts/esm/modules/mouse-wheel-zoom'),
          import('highcharts/esm/modules/stock'),
          // offline-exporting depends on exporting; load them sequentially to avoid a race
          import('highcharts/esm/modules/exporting').then(() => import('highcharts/esm/modules/offline-exporting')),
        ];
      },
    }),
  ],
};
